import { useRef } from "react"
import { getScenarioCostAssets } from "routes/scenarios/controller/scenario-cost-assets"
import { getScenarioCostSchedules } from "routes/scenarios/controller/scenario-cost-schedules"
import { BREAKDOWN_VIEW_ASSET } from "./cost-breakdown-view-utils"

/**
 * One keyset page of database-backed asset/schedule detail, with a per-state cursor
 * history so MUI's numbered prev/next pagination can walk keyset cursors: page N's
 * cursor is recorded from page N-1's endCursor as it loads. Changing the set key,
 * search, view or page size discards the history (callers must reset page to 0).
 *
 * IMPORTANT: mount per view (key the consuming component by view) — the underlying
 * query hook identity changes between asset and schedule modes.
 */
export function useCostDetailPage({ scenarioId, variantId, setKey, view, search, page, pageSize }) {
    const cursors = useRef({ key: "", byPage: {} })
    const stateKey = `${setKey}|${view}|${search || ""}|${pageSize}`
    if (cursors.current.key !== stateKey) {
        cursors.current = { key: stateKey, byPage: {} }
    }
    const after = page > 0 ? cursors.current.byPage[page] : undefined
    const controller = view === BREAKDOWN_VIEW_ASSET ? getScenarioCostAssets : getScenarioCostSchedules
    const { data, loading, error } = controller.useResults.status({
        scenarioId,
        variantId,
        setKey,
        first: pageSize,
        after,
        search: search || undefined,
    })
    if (data?.hasNextPage && data.endCursor) {
        cursors.current.byPage[page + 1] = data.endCursor
    }
    // error is returned so callers can distinguish a failure (stale generation,
    // authorisation, network) from a genuinely empty page. Rendering both as an empty
    // table would quietly imply a scenario has no assets or schedules.
    return { pageData: data, loading, error }
}
